import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import { LogIn, LogOut, ShieldCheck, ChevronDown } from "lucide-react";
import { useT } from "./i18n";

// Podłączamy plik firebase.js oraz nowe funkcje bazy danych
import { auth, db, loginWithGoogle, logoutUser } from "./firebase";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";

const AuthCtx = createContext(null);

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        try {
          // Odnosimy się do dokumentu gracza w bazie danych (tabela "users")
          const userRef = doc(db, "users", firebaseUser.uid);
          const docSnap = await getDoc(userRef);
          
          let adFreeUntil = 0;

          if (docSnap.exists()) {
            // Jeśli gracz istnieje w bazie, pobieramy jego czas braku reklam
            adFreeUntil = docSnap.data().ad_free_until || 0;
          } else {
            // Jeśli to pierwsze logowanie, tworzymy nowy wpis na serwerze
            await setDoc(userRef, {
              email: firebaseUser.email,
              name: firebaseUser.displayName,
              ad_free_until: 0,
              createdAt: Date.now()
            });
          }

          setUser({
            uid: firebaseUser.uid,
            email: firebaseUser.email,
            name: firebaseUser.displayName,
            picture: firebaseUser.photoURL,
            ad_free_until: adFreeUntil
          });
        } catch (err) {
          console.error("Błąd pobierania danych z bazy:", err);
        }
      } else {
        setUser(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  const login = useCallback(async () => {
    try {
      await loginWithGoogle();
    } catch (error) {
      console.warn("Logowanie przerwane lub błąd:", error);
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await logoutUser();
    } catch (error) {
      console.warn("Wylogowanie błąd:", error);
    }
    setUser(null);
  }, []);

  const value = useMemo(() => ({ user, setUser, loading, login, logout }), [user, loading, login, logout]);
  return <AuthCtx.Provider value={value}>{children}</AuthCtx.Provider>;
}

export function useAuth() {
  return useContext(AuthCtx);
}

function fmtLeft(until) {
  const s = (until || 0) - Date.now() / 1000;
  if (s <= 0) return null;
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  return d > 0 ? `${d}d ${h}h` : `${h}h`;
}

export function AccountWidget() {
  const { user, loading, login, logout } = useAuth();
  const { t } = useT();
  const [open, setOpen] = useState(false);
  if (loading) return null;

  if (!user) {
    return (
      <div className="fixed bottom-4 left-24 z-[60]">
        <button
          data-testid="google-login-button"
          onClick={login}
          className="flex items-center gap-2 px-4 py-2.5 rounded-full bg-white text-[#050510] font-bold text-sm shadow-xl hover:-translate-y-0.5 transition-transform"
        >
          <LogIn size={16} /> {t("login_google")}
        </button>
      </div>
    );
  }

  const adFree = fmtLeft(user.ad_free_until);
  return (
    <div className="fixed bottom-4 left-24 z-[60]" data-testid="account-widget">
      {open && (
        <div className="absolute bottom-14 left-0 w-56 p-3 rounded-2xl bg-[#12121A] border-2 border-white/15 shadow-2xl pop-in">
          <div className="text-sm font-bold truncate">{user.name || user.email}</div>
          <div className="text-xs text-[#A0A0B0] truncate mb-3">{user.email}</div>
          {adFree ? (
            <div className="flex items-center gap-2 text-[#00FF66] text-xs font-bold mb-3">
              <ShieldCheck size={14} /> {t("adfree_account")} · {adFree}
            </div>
          ) : (
            <div className="text-xs text-[#505060] mb-3">{t("no_adfree")}</div>
          )}
          <button
            data-testid="logout-button"
            onClick={() => {
              logout();
              setOpen(false);
            }}
            className="w-full flex items-center justify-center gap-2 py-2 rounded-xl border-2 border-white/15 text-[#FF007F] font-bold text-sm hover:bg-[#FF007F]/10 transition-colors"
          >
            <LogOut size={15} /> {t("logout")}
          </button>
        </div>
      )}
      <button
        data-testid="account-button"
        onClick={() => setOpen((v) => !v)}
        className="flex items-center gap-2 pl-1 pr-3 py-1 rounded-full bg-[#12121A] border-2 border-white/15 hover:border-[#00FFFF] transition-colors shadow-xl"
      >
        {user.picture ? (
          <img src={user.picture} alt="" className="w-8 h-8 rounded-full" referrerPolicy="no-referrer" />
        ) : (
          <span className="w-8 h-8 rounded-full bg-[#FFE800] text-[#050510] flex items-center justify-center font-black">
            {(user.name || user.email).charAt(0).toUpperCase()}
          </span>
        )}
        <span className="text-sm font-bold max-w-[80px] truncate">{user.name || user.email}</span>
        {adFree && <ShieldCheck size={14} className="text-[#00FF66]" />}
        <ChevronDown size={14} className="text-[#A0A0B0]" />
      </button>
    </div>
  );
}